import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "bs_cart_v2"; // bumped — schema changed to variants

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch (e) {
            return [];
        }
    });
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = (product, variant, quantity = 1) => {
        setItems((prev) => {
            const existing = prev.find((i) => i.variant_id === variant.id);
            if (existing) {
                return prev.map((i) =>
                    i.variant_id === variant.id
                        ? { ...i, quantity: Math.min(99, i.quantity + quantity) }
                        : i,
                );
            }
            return [
                ...prev,
                {
                    variant_id: variant.id,
                    product_id: product.id,
                    name: product.name,
                    badge: product.badge,
                    image: product.image,
                    grind: variant.grind,
                    size: variant.size,
                    price: variant.price,
                    quantity,
                },
            ];
        });
        setIsOpen(true);
    };

    const updateQuantity = (variantId, quantity) => {
        if (quantity <= 0) {
            removeItem(variantId);
            return;
        }
        setItems((prev) =>
            prev.map((i) =>
                i.variant_id === variantId
                    ? { ...i, quantity: Math.min(99, quantity) }
                    : i,
            ),
        );
    };

    const removeItem = (variantId) => {
        setItems((prev) => prev.filter((i) => i.variant_id !== variantId));
    };

    const clearCart = () => setItems([]);

    const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

    const value = useMemo(
        () => ({
            items,
            addItem,
            updateQuantity,
            removeItem,
            clearCart,
            totalCount,
            subtotal,
            isOpen,
            setIsOpen,
        }),
        [items, totalCount, subtotal, isOpen],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
